import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Search, Calendar, ArrowRight, ArrowLeft, Tag, Loader2, Clock, Eye, TrendingUp, ChevronRight, Sparkles, Share2, Linkedin, Twitter, Facebook, Link2, Check, X } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { blogAPI } from '../services/api';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format view count for display
const formatViews = (count) => {
  if (!count) return '0';
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return count.toString();
};

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
  }
};

const BlogListIntegrated = () => {
  const [posts, setPosts] = useState([]);
  const [popularPosts, setPopularPosts] = useState([]);
  const [featuredPost, setFeaturedPost] = useState(null);
  const [categories, setCategories] = useState(['all']);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    loadBlogPosts();
    loadCategories();
    loadPopularPosts();
  }, [selectedCategory, searchQuery]);

  const loadBlogPosts = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      if (searchQuery) {
        params.search = searchQuery;
      }
      const data = await blogAPI.getAllPosts(params);
      if (data.length > 0) {
        setFeaturedPost(data[0]);
        setPosts(data.slice(1));
      } else {
        setFeaturedPost(null);
        setPosts([]);
      }
    } catch (error) {
      toast.error('Failed to load blog posts');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const cats = await blogAPI.getCategories();
      // Filter out empty categories
      const validCats = cats.filter(c => c && c.trim() !== '');
      setCategories(['all', ...validCats]);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadPopularPosts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/blog/popular?limit=4`);
      if (res.ok) {
        const data = await res.json();
        setPopularPosts(data);
      }
    } catch (error) {
      console.error('Failed to load popular posts:', error);
    }
  };

  return (
    <div className="min-h-screen pt-20 bg-[#F8F9FA]">
      {/* Hero Section - Modern Split Layout */}
      <section className="relative bg-[#320547] overflow-hidden">
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,#95228A_0%,transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,#95228A_0%,transparent_40%)] opacity-50" />
        
        {/* Decorative Elements */}
        <div className="absolute top-20 right-20 w-72 h-72 border border-white/10 rounded-full" />
        <div className="absolute top-32 right-32 w-48 h-48 border border-white/5 rounded-full" />
        <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left - Typography */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="inline-flex items-center gap-2 text-white/60 text-xs uppercase tracking-[0.2em] mb-6">
                <Sparkles size={14} />
                Insights & Perspectives
              </span>
              <h1 className="font-['Playfair_Display'] text-5xl md:text-7xl font-bold text-white leading-[1.1] tracking-tight mb-6">
                Stories that
                <span className="block text-white/40">inspire growth</span>
              </h1>
              <p className="text-white/70 text-lg max-w-md leading-relaxed">
                Explore cutting-edge insights on technology, digital transformation, and business innovation.
              </p>
            </motion.div>
            
            {/* Right - Featured Card Preview */}
            {featuredPost && (
              <motion.div
                initial={{ opacity: 0, x: 30, rotateY: -10 }}
                animate={{ opacity: 1, x: 0, rotateY: 0 }}
                transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                className="hidden md:block"
              >
                <Link to={`/blog/${featuredPost.slug}`}>
                  <div className="relative group perspective-1000">
                    <div className="absolute -inset-4 bg-gradient-to-r from-[#95228A]/20 to-transparent rounded-3xl blur-2xl group-hover:blur-3xl transition-all" />
                    <div className="relative bg-white/10 backdrop-blur-sm rounded-2xl p-3 border border-white/10 transform group-hover:-translate-y-2 transition-transform duration-500">
                      <div className="relative h-48 rounded-xl overflow-hidden">
                        <img
                          src={featuredPost.image || 'https://images.pexels.com/photos/18122384/pexels-photo-18122384.jpeg'}
                          alt={featuredPost.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <Badge className="absolute bottom-3 left-3 bg-[#95228A] text-white">
                          {featuredPost.category || 'Featured'}
                        </Badge>
                      </div>
                      <div className="p-4">
                        <h3 className="font-['Playfair_Display'] text-xl font-semibold text-white line-clamp-2 mb-2">
                          {featuredPost.title}
                        </h3>
                        <div className="flex items-center gap-3 text-white/50 text-sm">
                          <span className="flex items-center gap-1">
                            <Eye size={14} />
                            {formatViews(featuredPost.view_count)}
                          </span>
                          <span>{featuredPost.read_time}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Filter Bar - Glassmorphism */}
      <div className="sticky top-[72px] z-40 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <Input
                type="text"
                placeholder="Search articles..."
                className="pl-11 h-11 bg-gray-50/80 border-gray-200/50 rounded-full focus:border-[#320547] focus:ring-[#320547]/20"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="blog-search-input"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0 w-full sm:w-auto justify-start sm:justify-end scrollbar-hide">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  data-testid={`category-${category}`}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                    selectedCategory === category
                      ? 'bg-[#320547] text-white shadow-lg shadow-[#320547]/20'
                      : 'bg-transparent text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {category === 'all' ? 'All Posts' : category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Bento Grid */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="flex justify-center items-center py-32">
              <div className="relative">
                <Loader2 className="animate-spin text-[#320547]" size={48} />
                <div className="absolute inset-0 animate-ping">
                  <Loader2 className="text-[#320547]/30" size={48} />
                </div>
              </div>
            </div>
          ) : !featuredPost ? (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-32"
            >
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-8">
                <Search size={40} className="text-gray-300" />
              </div>
              <h3 className="font-['Playfair_Display'] text-3xl font-bold text-gray-900 mb-3">No articles found</h3>
              <p className="text-gray-500 text-lg">
                {searchQuery || selectedCategory !== 'all' 
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Check back soon for new content!'}
              </p>
            </motion.div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 md:grid-cols-12 gap-6 auto-rows-[minmax(200px,auto)]"
            >
              {/* Featured Post - Large */}
              <motion.div 
                variants={itemVariants}
                className="col-span-1 md:col-span-8 md:row-span-2"
              >
                <Link to={`/blog/${featuredPost.slug}`} className="block h-full group">
                  <div className="relative h-full min-h-[400px] md:min-h-full rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] transition-shadow duration-500">
                    <img
                      src={featuredPost.image || 'https://images.pexels.com/photos/18122384/pexels-photo-18122384.jpeg'}
                      alt={featuredPost.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                    
                    {/* Content Overlay */}
                    <div className="absolute inset-0 p-8 flex flex-col justify-end">
                      <div className="flex gap-2 mb-4">
                        <Badge className="bg-white/20 backdrop-blur-sm text-white border-0">
                          Featured
                        </Badge>
                        {featuredPost.category && (
                          <Badge className="bg-[#95228A] text-white">
                            {featuredPost.category}
                          </Badge>
                        )}
                      </div>
                      <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-white mb-4 group-hover:text-white/90 transition-colors leading-tight">
                        {featuredPost.title}
                      </h2>
                      <p className="text-white/70 text-base mb-6 line-clamp-2 max-w-2xl">
                        {featuredPost.excerpt}
                      </p>
                      <div className="flex items-center gap-6 text-white/60 text-sm">
                        <div className="flex items-center gap-2">
                          {featuredPost.author_image && (
                            <img
                              src={featuredPost.author_image}
                              alt={featuredPost.author}
                              className="w-10 h-10 rounded-full object-cover ring-2 ring-white/20"
                            />
                          )}
                          <span className="font-medium text-white/80">{featuredPost.author}</span>
                        </div>
                        <span className="w-1 h-1 bg-white/40 rounded-full" />
                        <span>{featuredPost.date || new Date(featuredPost.created_at).toLocaleDateString()}</span>
                        <span className="w-1 h-1 bg-white/40 rounded-full" />
                        <span className="flex items-center gap-1">
                          <Eye size={14} />
                          {formatViews(featuredPost.view_count)} views
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>

              {/* Popular Posts - Sidebar */}
              <motion.div 
                variants={itemVariants}
                className="col-span-1 md:col-span-4 md:row-span-2 bg-white p-6 rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-[#320547] rounded-xl flex items-center justify-center">
                    <TrendingUp size={18} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-['Playfair_Display'] text-xl font-bold text-gray-900">Trending</h3>
                    <p className="text-gray-500 text-xs">Most read this week</p>
                  </div>
                </div>
                
                {popularPosts.length > 0 ? (
                  <div className="space-y-4">
                    {popularPosts.map((post, index) => (
                      <Link 
                        key={post.id} 
                        to={`/blog/${post.slug}`}
                        className="group flex gap-3 items-start p-2 -mx-2 rounded-xl hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden">
                          <img 
                            src={post.image || 'https://images.pexels.com/photos/8837488/pexels-photo-8837488.jpeg'} 
                            alt={post.title}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-gray-900 group-hover:text-[#95228A] transition-colors line-clamp-2 mb-1 leading-snug">
                            {post.title}
                          </h4>
                          <div className="flex items-center gap-2 text-xs text-gray-400">
                            <span className="flex items-center gap-1">
                              <Eye size={12} />
                              {formatViews(post.view_count)}
                            </span>
                            <span>•</span>
                            <span>{post.read_time}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-8">No trending posts yet</p>
                )}
              </motion.div>

              {/* Rest of Posts - Grid */}
              {posts.map((post, index) => (
                <motion.div
                  key={post.id}
                  variants={itemVariants}
                  className="col-span-1 md:col-span-4"
                >
                  <Link to={`/blog/${post.slug}`} className="block h-full group">
                    <div className="h-full bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:-translate-y-1 transition-all duration-500">
                      <div className="relative h-44 overflow-hidden">
                        <img
                          src={post.image || 'https://images.pexels.com/photos/8837488/pexels-photo-8837488.jpeg'}
                          alt={post.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                        {post.category && (
                          <Badge className="absolute top-4 left-4 bg-white/90 text-[#320547] backdrop-blur-sm border-0 shadow-sm">
                            {post.category}
                          </Badge>
                        )}
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
                          <span>{post.date || new Date(post.created_at).toLocaleDateString()}</span>
                          <span className="w-1 h-1 bg-gray-300 rounded-full" />
                          <span className="flex items-center gap-1">
                            <Eye size={12} />
                            {formatViews(post.view_count)}
                          </span>
                        </div>
                        <h3 className="font-['Playfair_Display'] text-lg font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-[#95228A] transition-colors leading-snug">
                          {post.title}
                        </h3>
                        <p className="text-gray-500 text-sm line-clamp-2 mb-4">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                          <div className="flex items-center gap-2">
                            {post.author_image && (
                              <img
                                src={post.author_image}
                                alt={post.author}
                                className="w-7 h-7 rounded-full object-cover"
                              />
                            )}
                            <span className="text-xs text-gray-500 font-medium">{post.author}</span>
                          </div>
                          <span className="text-[#320547] text-sm font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            Read <ArrowRight size={14} />
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}

              {/* Newsletter CTA - Embedded in Grid */}
              <motion.div 
                variants={itemVariants}
                className="col-span-1 md:col-span-4 bg-[#320547] p-8 rounded-2xl flex flex-col justify-center relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#95228A]/30 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full blur-2xl" />
                <div className="relative z-10">
                  <span className="text-white/60 text-xs uppercase tracking-widest">Newsletter</span>
                  <h3 className="font-['Playfair_Display'] text-2xl font-bold text-white mt-2 mb-3">Stay ahead of the curve</h3>
                  <p className="text-white/60 text-sm mb-6">Get weekly insights delivered to your inbox.</p>
                  <Button asChild className="w-full bg-[#95228A] hover:bg-[#7A1B70] text-white rounded-full h-11 font-medium">
                    <Link to="/contact">Subscribe Now</Link>
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

const BlogPostIntegrated = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadBlogPost();
  }, [slug]);

  const loadBlogPost = async () => {
    try {
      setLoading(true);
      const data = await blogAPI.getPostBySlug(slug);
      setPost(data);
      
      // Increment view count
      try {
        await fetch(`${API_URL}/api/blog/posts/${slug}/view`, { method: 'POST' });
      } catch (e) {
        console.error('Failed to increment view count:', e);
      }
      
      // Load all posts for sidebar
      const posts = await blogAPI.getAllPosts();
      setAllPosts(posts.filter(p => p.slug !== slug && p.is_published !== false));
      const related = posts
        .filter(p => p.slug !== slug && p.is_published !== false)
        .slice(0, 3);
      setRelatedPosts(related);
    } catch (error) {
      console.error('Failed to load blog post:', error);
    } finally {
      setLoading(false);
    }
  };

  // Share URLs - use /api/share/blog/ URL for social platforms for proper meta tags
  const getShareUrl = (platform) => {
    // Use /api/share/blog/{slug} which serves HTML with proper OG tags
    const ogUrl = `https://navarisdigital.com/api/share/blog/${slug}`;
    const directUrl = `https://navarisdigital.com/blog/${slug}`;
    const title = encodeURIComponent(post?.title || '');
    const excerpt = encodeURIComponent(post?.excerpt || post?.title || '');
    
    switch (platform) {
      case 'linkedin':
        return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(ogUrl)}`;
      case 'twitter':
        return `https://twitter.com/intent/tweet?url=${encodeURIComponent(ogUrl)}&text=${title}`;
      case 'facebook':
        return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(ogUrl)}`;
      case 'whatsapp':
        return `https://wa.me/?text=${title}%20${encodeURIComponent(ogUrl)}`;
      case 'email':
        return `mailto:?subject=${title}&body=${excerpt}%0A%0ARead more: ${encodeURIComponent(directUrl)}`;
      default:
        return directUrl;
    }
  };

  const handleShare = (platform) => {
    const url = getShareUrl(platform);
    if (platform === 'email') {
      window.location.href = url;
    } else {
      window.open(url, '_blank', 'width=600,height=400');
    }
    setShowShareMenu(false);
  };

  const handleCopyLink = () => {
    // Use /api/share/blog/{slug} for sharing - it has proper OG tags
    const shareUrl = `https://navarisdigital.com/api/share/blog/${slug}`;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Shareable link copied! This link shows proper preview on social media.');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-[#F8F9FA]">
        <div className="relative">
          <Loader2 className="animate-spin text-[#320547]" size={48} />
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <h2 className="font-['Playfair_Display'] text-4xl font-bold text-gray-900 mb-4">Post Not Found</h2>
          <p className="text-gray-500 mb-8">The article you're looking for doesn't exist.</p>
          <Button asChild className="bg-[#320547] hover:bg-[#95228A] rounded-full px-8">
            <Link to="/blog">Back to Blog</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-[#F8F9FA]">
      {/* SEO with blog excerpt for social sharing */}
      <SEO 
        title={post.title}
        description={post.excerpt || post.title}
        canonicalUrl={`/blog/${post.slug}`}
        ogImage={post.image || 'https://navarisdigital.com/og-image.jpg'}
        type="article"
        author={post.author?.name || 'Navaris Digital'}
        publishedTime={post.created_at}
        modifiedTime={post.updated_at}
      />
      
      {/* Google News Subscribe with Google */}
      <Helmet>
        <script async type="application/javascript" src="https://news.google.com/swg/js/v1/swg-basic.js"></script>
        <script>
          {`
            (self.SWG_BASIC = self.SWG_BASIC || []).push(function(basicSubscriptions) {
              basicSubscriptions.init({
                type: "NewsArticle",
                isPartOfType: ["Product"],
                isPartOfProductId: "CAow26jEDA:openaccess",
                clientOptions: { theme: "light", lang: "en-GB" },
              });
            });
          `}
        </script>
      </Helmet>
      
      {/* Hero Section */}
      <section className="relative h-[400px] md:h-[500px] overflow-hidden">
        <img
          src={post.image || 'https://images.pexels.com/photos/18122384/pexels-photo-18122384.jpeg'}
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20" />
        
        {/* Content */}
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-6 md:px-12 pb-12 w-full">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl"
            >
              {post.category && (
                <Badge className="bg-[#95228A] text-white mb-4">{post.category}</Badge>
              )}
              <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                {post.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm">
                <div className="flex items-center gap-2">
                  {post.author_image && (
                    <img
                      src={post.author_image}
                      alt={post.author}
                      className="w-8 h-8 rounded-full object-cover ring-2 ring-white/30"
                    />
                  )}
                  <span className="font-medium text-white">{post.author}</span>
                </div>
                <div className="h-4 w-px bg-white/30 hidden sm:block" />
                <div className="flex items-center gap-1">
                  <Calendar size={14} />
                  <span>{post.date || new Date(post.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock size={14} />
                  <span>{post.read_time || '5 min read'}</span>
                </div>
                <div className="flex items-center gap-1 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full">
                  <Eye size={14} />
                  <span>{formatViews(post.view_count || 0)} views</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Article Content */}
            <div className="lg:w-2/3">
              <Button
                asChild
                variant="ghost"
                className="mb-8 text-gray-500 hover:text-[#320547] -ml-4"
              >
                <Link to="/blog">
                  <ArrowLeft className="mr-2" size={18} /> Back to Blog
                </Link>
              </Button>

              {/* Share Buttons */}
              <div className="flex items-center gap-3 mb-8 pb-8 border-b border-gray-200">
                <span className="text-gray-500 font-medium flex items-center gap-2">
                  <Share2 size={18} /> Share:
                </span>
                <button
                  onClick={() => handleShare('linkedin')}
                  className="p-2 rounded-full bg-[#0077B5] text-white hover:bg-[#005582] transition-colors"
                  title="Share on LinkedIn"
                >
                  <Linkedin size={18} />
                </button>
                <button
                  onClick={() => handleShare('twitter')}
                  className="p-2 rounded-full bg-[#1DA1F2] text-white hover:bg-[#0d8ddb] transition-colors"
                  title="Share on Twitter"
                >
                  <Twitter size={18} />
                </button>
                <button
                  onClick={() => handleShare('facebook')}
                  className="p-2 rounded-full bg-[#1877F2] text-white hover:bg-[#0d65d9] transition-colors"
                  title="Share on Facebook"
                >
                  <Facebook size={18} />
                </button>
                <button
                  onClick={() => handleShare('whatsapp')}
                  className="p-2 rounded-full bg-[#25D366] text-white hover:bg-[#1da851] transition-colors"
                  title="Share on WhatsApp"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-[18px] h-[18px]">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </button>
                <button
                  onClick={handleCopyLink}
                  className={`p-2 rounded-full transition-colors ${copied ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                  title="Copy Link"
                >
                  {copied ? <Check size={18} /> : <Link2 size={18} />}
                </button>
              </div>

              <motion.article 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="prose prose-lg max-w-none"
              >
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({node, ...props}) => <h1 className="font-['Playfair_Display'] text-4xl font-bold text-gray-900 mt-12 mb-6" {...props} />,
                    h2: ({node, ...props}) => <h2 className="font-['Playfair_Display'] text-3xl font-bold text-gray-900 mt-10 mb-5" {...props} />,
                    h3: ({node, ...props}) => <h3 className="font-['Playfair_Display'] text-2xl font-bold text-gray-900 mt-8 mb-4" {...props} />,
                    p: ({node, ...props}) => <p className="text-gray-600 mb-6 leading-relaxed text-lg" {...props} />,
                    ul: ({node, ...props}) => <ul className="list-disc mb-6 text-gray-600 space-y-3 ml-6" {...props} />,
                    ol: ({node, ...props}) => <ol className="list-decimal mb-6 text-gray-600 space-y-3 ml-6" {...props} />,
                    li: ({node, ...props}) => <li className="text-gray-600 text-lg leading-relaxed" {...props} />,
                    strong: ({node, ...props}) => <strong className="font-semibold text-gray-900" {...props} />,
                    em: ({node, ...props}) => <em className="italic" {...props} />,
                    blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-[#95228A] pl-6 py-1 my-8 bg-gradient-to-r from-[#320547]/5 to-transparent italic text-gray-700 rounded-r-xl" {...props} />,
                    code: ({node, inline, ...props}) => 
                      inline ? (
                        <code className="bg-[#320547]/10 px-2 py-1 rounded text-[#320547] text-sm font-mono" {...props} />
                      ) : (
                        <pre className="bg-[#1a1a2e] text-gray-100 p-6 rounded-2xl overflow-x-auto my-8">
                          <code className="text-sm font-mono" {...props} />
                        </pre>
                      ),
                    a: ({node, ...props}) => <a className="text-[#95228A] hover:text-[#320547] font-medium underline underline-offset-4 transition-colors" target="_blank" rel="noopener noreferrer" {...props} />,
                    hr: ({node, ...props}) => <hr className="my-12 border-gray-200" {...props} />,
                    img: ({node, ...props}) => <img className="rounded-2xl my-8 max-w-full shadow-xl" {...props} />,
                  }}
                >
                  {post.content}
                </ReactMarkdown>
              </motion.article>

              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="mt-12 pt-8 border-t border-gray-200">
                  <div className="flex items-center gap-3 flex-wrap">
                    <Tag size={18} className="text-gray-400" />
                    {post.tags.map((tag) => (
                      <span 
                        key={tag} 
                        className="px-4 py-2 bg-[#320547]/10 text-[#320547] rounded-full text-sm font-medium hover:bg-[#320547] hover:text-white transition-colors cursor-default"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Share Buttons Bottom */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <p className="text-gray-500 font-medium mb-4 flex items-center gap-2">
                  <Share2 size={18} /> Enjoyed this article? Share it!
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#0077B5] text-white hover:bg-[#005582] transition-colors text-sm font-medium"
                  >
                    <Linkedin size={16} /> LinkedIn
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#1DA1F2] text-white hover:bg-[#0d8ddb] transition-colors text-sm font-medium"
                  >
                    <Twitter size={16} /> Twitter
                  </button>
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#1877F2] text-white hover:bg-[#0d65d9] transition-colors text-sm font-medium"
                  >
                    <Facebook size={16} /> Facebook
                  </button>
                  <button
                    onClick={handleCopyLink}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors text-sm font-medium ${copied ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                  >
                    {copied ? <Check size={16} /> : <Link2 size={16} />}
                    {copied ? 'Copied!' : 'Copy Link'}
                  </button>
                </div>
              </div>

              {/* View Count Footer */}
              <div className="mt-8 pt-8 border-t border-gray-200 flex items-center gap-2 text-gray-400">
                <Eye size={18} />
                <span>{formatViews(post.view_count || 0)} people found this helpful</span>
              </div>

              {/* Blog Legal Links */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <Link 
                    to="/blog/privacy-policy" 
                    className="hover:text-[#95228A] transition-colors"
                  >
                    Privacy Policy
                  </Link>
                  <span className="text-gray-300">|</span>
                  <Link 
                    to="/blog/terms-of-use" 
                    className="hover:text-[#95228A] transition-colors"
                  >
                    Terms of Use
                  </Link>
                </div>
              </div>
            </div>

            {/* Sidebar - Other Blogs */}
            <div className="lg:w-1/3">
              <div className="sticky top-24">
                <h3 className="font-['Playfair_Display'] text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <Sparkles size={20} className="text-[#95228A]" />
                  More Articles
                </h3>
                <div className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto pr-2">
                  {allPosts.slice(0, 8).map((otherPost, index) => (
                    <motion.div
                      key={otherPost.id}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Link 
                        to={`/blog/${otherPost.slug}`} 
                        className="group flex gap-4 p-3 rounded-xl hover:bg-white hover:shadow-md transition-all duration-300"
                      >
                        <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden">
                          <img
                            src={otherPost.image || 'https://images.pexels.com/photos/8837488/pexels-photo-8837488.jpeg'}
                            alt={otherPost.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          {otherPost.category && (
                            <span className="text-xs font-medium text-[#95228A] uppercase tracking-wide">
                              {otherPost.category}
                            </span>
                          )}
                          <h4 className="font-semibold text-gray-900 text-sm line-clamp-2 group-hover:text-[#320547] transition-colors mt-1">
                            {otherPost.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                            <span className="flex items-center gap-1">
                              <Eye size={12} />
                              {formatViews(otherPost.view_count)}
                            </span>
                            <span>{otherPost.read_time || '5 min'}</span>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>

                {/* View All Link */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <Link 
                    to="/blog" 
                    className="flex items-center justify-center gap-2 w-full py-3 bg-[#320547] text-white rounded-xl hover:bg-[#95228A] transition-colors font-medium"
                  >
                    View All Articles <ArrowRight size={18} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts - Mobile/Tablet */}
      {relatedPosts.length > 0 && (
        <section className="py-16 bg-white lg:hidden">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center justify-between mb-10">
              <h3 className="font-['Playfair_Display'] text-3xl font-bold text-gray-900">Continue Reading</h3>
              <Link to="/blog" className="text-[#320547] font-medium flex items-center gap-1 hover:gap-2 transition-all">
                View All <ArrowRight size={18} />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedPosts.map((relatedPost, index) => (
                <motion.div
                  key={relatedPost.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={`/blog/${relatedPost.slug}`} className="block group">
                    <div className="rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-500 hover:-translate-y-1">
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={relatedPost.image || 'https://images.pexels.com/photos/8837488/pexels-photo-8837488.jpeg'}
                          alt={relatedPost.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                      </div>
                      <div className="p-6 bg-white">
                        {relatedPost.category && (
                          <Badge className="mb-3 bg-[#320547]/10 text-[#320547] hover:bg-[#320547] hover:text-white transition-colors">
                            {relatedPost.category}
                          </Badge>
                        )}
                        <h4 className="font-['Playfair_Display'] text-xl font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-[#95228A] transition-colors">
                          {relatedPost.title}
                        </h4>
                        <div className="flex items-center gap-3 text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <Eye size={14} />
                            {formatViews(relatedPost.view_count)}
                          </span>
                          <span>{relatedPost.read_time}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

// Reusable Related Blogs Component for Services/Industries pages
const RelatedBlogs = ({ serviceIds = [], industryIds = [], title = "Related Articles", limit = 3 }) => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRelatedBlogs = async () => {
      try {
        const res = await fetch(`${API_URL}/api/blog/posts`);
        if (res.ok) {
          const allPosts = await res.json();
          const relatedPosts = allPosts.filter(post => {
            if (post.is_published === false) return false;
            
            const matchesService = serviceIds.length > 0 && 
              post.related_services?.some(s => serviceIds.includes(s));
            const matchesIndustry = industryIds.length > 0 && 
              post.related_industries?.some(i => industryIds.includes(i));
            
            return matchesService || matchesIndustry;
          }).slice(0, limit);
          
          setBlogs(relatedPosts);
        }
      } catch (error) {
        console.error('Error fetching related blogs:', error);
      } finally {
        setLoading(false);
      }
    };

    if (serviceIds.length > 0 || industryIds.length > 0) {
      fetchRelatedBlogs();
    } else {
      setLoading(false);
    }
  }, [serviceIds, industryIds, limit]);

  if (loading) {
    return (
      <div className="py-12 flex justify-center">
        <Loader2 className="animate-spin text-[#320547]" size={32} />
      </div>
    );
  }

  if (blogs.length === 0) {
    return null;
  }

  return (
    <section className="py-16 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between mb-10">
          <h3 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-gray-900">{title}</h3>
          <Link to="/blog" className="text-[#320547] font-medium flex items-center gap-1 hover:gap-2 transition-all text-sm">
            View All <ChevronRight size={18} />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {blogs.map((post, index) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <Link to={`/blog/${post.slug}`} className="block group">
                <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:-translate-y-1 transition-all duration-500">
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={post.image || 'https://images.pexels.com/photos/8837488/pexels-photo-8837488.jpeg'}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    {post.category && (
                      <Badge className="absolute top-4 left-4 bg-white/90 text-[#320547] backdrop-blur-sm text-xs">
                        {post.category}
                      </Badge>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 text-xs text-gray-400 mb-2">
                      <span>{post.date || new Date(post.created_at).toLocaleDateString()}</span>
                      <span className="flex items-center gap-1">
                        <Eye size={12} />
                        {formatViews(post.view_count)}
                      </span>
                    </div>
                    <h4 className="font-['Playfair_Display'] text-lg font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-[#95228A] transition-colors">
                      {post.title}
                    </h4>
                    <p className="text-sm text-gray-500 line-clamp-2">{post.excerpt}</p>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export { BlogListIntegrated, BlogPostIntegrated, RelatedBlogs };
